import { create } from 'zustand';
import { createSessionConsumer, type SessionConsumerOptions, type SessionConsumerState } from '@borgee/remote-control-client';
import { IndexedDbSessionJournal } from '@borgee/remote-control-client/indexeddb';
import { bindDshSessionLifecycle } from '../lib/dsh-session-lifecycle';
import { useNormalizedActivityStore } from './useNormalizedActivityStore';
export { sessionKey as dshSessionKey, type SessionScope as DshSessionScope, type SessionView as DshSessionView, type SessionStorageError as DshStorageError } from '@borgee/remote-control-client';

export type DshSessionStoreOptions = Omit<SessionConsumerOptions, 'journal'> & { journal?: SessionConsumerOptions['journal'] };

/** React subscriptions and browser storage are application adapters to the instance consumer. */
export function createDshSessionStore(options: DshSessionStoreOptions = {}) {
  const consumer = createSessionConsumer({ ...options, journal: options.journal ?? new IndexedDbSessionJournal() });
  const store = create<SessionConsumerState>(() => consumer.getState());
  consumer.subscribe(state => store.setState(state));
  return store;
}

export const useDshSessionStore = createDshSessionStore({
  onPresentation: (scope, tasks, notice, resolve) => useNormalizedActivityStore.getState().applyPresentation(scope.channelId, scope.agentId, tasks, notice, resolve),
});

if (typeof window !== 'undefined' && typeof document !== 'undefined') {
  const unbind = bindDshSessionLifecycle(() => useDshSessionStore.getState().flush(), window, document);
  if (import.meta.hot) import.meta.hot.dispose(unbind);
}

/** Exact normalized targets require a verified local Session before mutation. */
export function isSessionControlPaused(channelId: string, agentId: string, bindingId?: string): boolean {
  const views = Object.entries(useDshSessionStore.getState().sessions).filter(([key]) => {
    const [, , channel, agent, binding] = JSON.parse(key);
    return channel === channelId && agent === agentId && (!bindingId || binding === bindingId);
  }).map(([, view]) => view);
  return !!bindingId && !views.length || views.some(view => view.servicePaused === true);
}
